// src/utils/LevelManager.js
import { TEAMS } from './constants';
import { createUnit } from '../models/UnitTypes';

// Level definitions
export const LEVELS = [
  {
    name: "Frontier Outpost",
    stages: [
      {
        name: "Stage 1-1",
        description: "Scout the enemy frontier",
        startingCurrency: 300,
        units: [
          { type: 'torch', x: 0.75, y: 0.3 },
          { type: 'torch', x: 0.75, y: 0.7 }
        ]
      },
      {
        name: "Stage 1-2",
        description: "Take out the enemy patrol",
        startingCurrency: 350,
        units: [
          { type: 'torch', x: 0.75, y: 0.3 },
          { type: 'torch', x: 0.75, y: 0.7 },
          { type: 'barrel', x: 0.85, y: 0.5 }
        ]
      },
      {
        name: "Stage 1-3",
        description: "Face the explosive carriers",
        startingCurrency: 400,
        units: [
          { type: 'torch', x: 0.75, y: 0.3 },
          { type: 'torch', x: 0.75, y: 0.7 },
          { type: 'barrel', x: 0.85, y: 0.5 },
          { type: 'bombardCrew', x: 0.82, y: 0.4 }
        ]
      }
    ]
  },
  {
    name: "Mountain Fortress",
    stages: [
      {
        name: "Stage 2-1",
        description: "Enter the mountain pass",
        startingCurrency: 650,
        units: [
          { type: 'torch', x: 0.75, y: 0.3 },
          { type: 'torch', x: 0.75, y: 0.7 },
          { type: 'barrel', x: 0.8, y: 0.5 },
          { type: 'bombardCrew', x: 0.85, y: 0.4 }
        ]
      },
      {
        name: "Stage 2-2",
        description: "Face the first defensive line",
        startingCurrency: 700,
        units: [
          { type: 'torch', x: 0.7, y: 0.2 },
          { type: 'torch', x: 0.7, y: 0.5 },
          { type: 'torch', x: 0.7, y: 0.8 },
          { type: 'bombardCrew', x: 0.8, y: 0.35 },
          { type: 'woodtower', x: 0.9, y: 0.5 }
        ]
      }
    ]
  }
];

export class LevelManager {
  constructor(Unit) {
    this.currentLevel = 0;
    this.currentStage = 0;
    this.levels = LEVELS;
    this.Unit = Unit;
  }

  // Get current stage info
  getCurrentStageInfo() {
    return {
      levelNumber: this.currentLevel + 1,
      levelName: this.levels[this.currentLevel].name,
      stageNumber: this.currentStage + 1,
      totalStages: this.levels[this.currentLevel].stages.length,
      stageName: this.levels[this.currentLevel].stages[this.currentStage].name,
      stageDescription: this.levels[this.currentLevel].stages[this.currentStage].description,
      totalLevels: this.levels.length
    };
  }

  // Load current stage
  loadCurrentStage(canvasWidth, canvasHeight) {
    const level = this.levels[this.currentLevel];
    const stage = level.stages[this.currentStage];
    
    const units = {
      red: [], // Player units start empty
      blue: [] // Enemy units from stage config
    };

    // Create enemy units
    for (const unit of stage.units) {
      const x = Math.floor(unit.x * canvasWidth);
      const y = Math.floor(unit.y * canvasHeight);
      
      const enemyUnit = createUnit(unit.type, TEAMS.BLUE, x, y, this.Unit);
      units.blue.push(enemyUnit);
    }

    return {
      units,
      startingCurrency: stage.startingCurrency
    };
  }

  // Advance to next stage/level
  advanceStage() {
    this.currentStage++;
    
    // Check if we've completed all stages in this level
    if (this.currentStage >= this.levels[this.currentLevel].stages.length) {
      // Move to next level, stage 0
      this.currentLevel++;
      this.currentStage = 0;
      
      // Check if we've completed all levels
      if (this.currentLevel >= this.levels.length) {
        // Game complete, reset to first level
        this.currentLevel = 0;
        return "GAME_COMPLETE";
      }
      
      return "LEVEL_COMPLETE";
    }
    
    return "STAGE_ADVANCED";
  }

  // Go back to previous stage/level
  retreatStage() {
    this.currentStage--;
    
    // Check if we need to go back to the previous level
    if (this.currentStage < 0) {
      this.currentLevel--;
      
      // Check if we're before the first level
      if (this.currentLevel < 0) {
        // Reset to the first level, first stage
        this.currentLevel = 0;
        this.currentStage = 0;
        return "GAME_RESET";
      }
      
      // Set to the last stage of the previous level
      this.currentStage = this.levels[this.currentLevel].stages.length - 1;
      return "LEVEL_RETREATED";
    }
    
    return "STAGE_RETREATED";
  }

  // Check if at first stage of first level
  isFirstStage() {
    return this.currentLevel === 0 && this.currentStage === 0;
  }

  // Reset to first level, first stage
  reset() {
    this.currentLevel = 0;
    this.currentStage = 0;
  }
}